import { useEffect } from 'react';

/**
 * 安卓实体返回键接管（只在打包成 App 时生效，网页端完全是空操作）。
 *
 * 为什么必须自己处理：Capacitor 的 BridgeActivity **没有任何返回键逻辑**，
 * 默认走 Android 的 finish()，也就是不管当前在第几层页面，按返回直接退出应用。
 * 学生从「任务 → 任务详情 → 资料」点进去后按返回会整个退出，体验无法接受。
 *
 * 规则：
 *  · 还有历史可回退 → 回退一步（等价于浏览器返回）
 *  · 已在最外层 → 双击返回退出，避免误触直接杀掉应用
 *
 * 插件用动态 import：网页端不会把它打进首屏 chunk。
 */
const DOUBLE_TAP_MS = 2000;

export function useAndroidBackButton() {
  useEffect(() => {
    let dispose: (() => void) | undefined;
    let cancelled = false;
    let lastPress = 0;

    (async () => {
      const { Capacitor } = await import('@capacitor/core');
      if (!Capacitor.isNativePlatform() || cancelled) return;

      const { App } = await import('@capacitor/app');
      const handle = await App.addListener('backButton', ({ canGoBack }) => {
        // canGoBack 由 WebView 的历史栈判断，与 react-router 的 history 一致
        if (canGoBack || window.history.length > 1) {
          window.history.back();
          return;
        }
        const now = Date.now();
        if (now - lastPress < DOUBLE_TAP_MS) {
          void App.exitApp();
        } else {
          lastPress = now;
          showExitHint();
        }
      });

      if (cancelled) { void handle.remove(); return; }
      dispose = () => { void handle.remove(); };
    })();

    return () => { cancelled = true; dispose?.(); };
  }, []);
}

/** 轻量 toast：不依赖任何 UI 库，避免和网页端的 Toast 体系耦合 */
function showExitHint() {
  const id = '__exit_hint__';
  if (document.getElementById(id)) return;
  const el = document.createElement('div');
  el.id = id;
  el.textContent = '再按一次返回键退出';
  Object.assign(el.style, {
    position: 'fixed', left: '50%', bottom: 'calc(72px + env(safe-area-inset-bottom))',
    transform: 'translateX(-50%)', zIndex: '99999',
    background: 'rgba(44,52,40,0.86)', color: '#fff',
    padding: '9px 20px', borderRadius: '999px', fontSize: '13px',
    fontFamily: '"Noto Serif SC", serif', pointerEvents: 'none',
  } as CSSStyleDeclaration);
  document.body.appendChild(el);
  setTimeout(() => el.remove(), DOUBLE_TAP_MS);
}
